import React, { useState, useEffect, useRef } from 'react';
import { X, Save, ImagePlus, Trash2 } from 'lucide-react';
import { Compte, updateCompte } from '../lib/services/AccountService';
import { DEFAULT_COLORS, COLOR_PALETTE } from '../config/constants';
import { cleanNumericInput, parseNumericValue, isValidNumericValue } from '../lib/utils/numberUtils';
import {
  uploadTransactionAttachment,
  removeTransactionAttachmentFile,
  getTransactionAttachmentSignedUrl,
} from '../lib/services/transactionAttachmentService';

interface AccountFormProps {
  account?: Compte | undefined;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    account: Compte,
  ) => Promise<{ success: boolean; error?: string; data?: Compte }>;
  title?: string;
}

const AccountForm: React.FC<AccountFormProps> = ({
  account,
  isOpen,
  onClose,
  onSubmit,
  title = 'Nouveau compte',
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    solde: '' as string | number,
    solde_initial: '' as string | number,
    couleur: DEFAULT_COLORS.ACCOUNT,
    type: 'compte_courant' as 'compte_courant' | 'epargne' | 'credit' | 'autre',
    iban: '',
    bic: '',
    numero_compte: '',
    nom_banque: '',
    site_web: '',
    actif: true,
    is_default: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachmentPath, setAttachmentPath] = useState<string | null>(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null);
  const [pendingIconFile, setPendingIconFile] = useState<File | null>(null);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (account) {
      setFormData({
        nom: account.nom || '',
        description: account.description || '',
        solde: (account.solde || 0).toString(),
        solde_initial: (account.solde_initial || 0).toString(),
        couleur: account.couleur || '#3B82F6',
        type: account.type || 'compte_courant',
        iban: account.iban || '',
        bic: account.bic || '',
        numero_compte: account.numero_compte || '',
        nom_banque: account.nom_banque || '',
        site_web: account.site_web || '',
        actif: account.actif !== false,
        is_default: account.is_default === true,
      });
      setAttachmentPath(account.attachment_path ?? null);
    } else {
      setFormData({
        nom: '',
        description: '',
        solde: '',
        solde_initial: '',
        couleur: DEFAULT_COLORS.ACCOUNT,
        type: 'compte_courant',
        iban: '',
        bic: '',
        numero_compte: '',
        nom_banque: '',
        site_web: '',
        actif: true,
        is_default: false,
      });
      setAttachmentPath(null);
    }
    setPendingIconFile(null);
    setAttachmentPreviewUrl(null);
    setError(null);
  }, [account, isOpen]);

  useEffect(() => {
    let cancelled = false;
    if (pendingIconFile) {
      const url = URL.createObjectURL(pendingIconFile);
      setAttachmentPreviewUrl(url);
      return () => {
        cancelled = true;
        URL.revokeObjectURL(url);
      };
    }
    if (!attachmentPath) {
      setAttachmentPreviewUrl(null);
      return;
    }
    (async () => {
      const { url } = await getTransactionAttachmentSignedUrl(attachmentPath);
      if (!cancelled) setAttachmentPreviewUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [attachmentPath, pendingIconFile]);

  const handleInputChange = (field: keyof typeof formData, value: string | number | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const commitIconForId = async (compteId: string, file: File, previousPath: string | null) => {
    const { path, error: upErr } = await uploadTransactionAttachment(compteId, file, 'comptes');
    if (upErr || !path) {
      return { error: upErr || "Échec de l'envoi de l'icône" };
    }
    const { error: updErr } = await updateCompte(compteId, { attachment_path: path });
    if (updErr) {
      await removeTransactionAttachmentFile(path);
      return {
        error:
          updErr && typeof updErr === 'object' && 'message' in updErr
            ? String((updErr as { message: unknown }).message)
            : "L'icône n'a pas pu être liée",
      };
    }
    if (previousPath && previousPath !== path) {
      await removeTransactionAttachmentFile(previousPath);
    }
    return { path, error: null as string | null };
  };

  const handleIconPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!account?.id) {
      setPendingIconFile(file);
      setAttachmentPath(null);
      return;
    }

    setAttachmentBusy(true);
    setError(null);
    try {
      const previous = attachmentPath;
      const { path, error: commitErr } = await commitIconForId(account.id, file, previous);
      if (commitErr || !path) {
        setError(commitErr || "Échec de l'envoi de l'icône");
        return;
      }
      setAttachmentPath(path);
      setPendingIconFile(null);
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleRemoveIcon = async () => {
    if (pendingIconFile) {
      setPendingIconFile(null);
      setAttachmentPreviewUrl(null);
      return;
    }
    if (!attachmentPath) return;

    if (!account?.id) {
      setAttachmentPath(null);
      setAttachmentPreviewUrl(null);
      return;
    }

    setAttachmentBusy(true);
    setError(null);
    try {
      const path = attachmentPath;
      const { error: updErr } = await updateCompte(account.id, { attachment_path: null });
      if (updErr) {
        setError(
          updErr && typeof updErr === 'object' && 'message' in updErr
            ? String((updErr as { message: unknown }).message)
            : "Impossible de retirer l'icône",
        );
        return;
      }
      await removeTransactionAttachmentFile(path);
      setAttachmentPath(null);
      setAttachmentPreviewUrl(null);
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nom.trim()) {
      setError('Le nom du compte est obligatoire');
      return;
    }

    if (!isValidNumericValue(formData.solde)) {
      setError('Le solde doit être un nombre valide');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const soldeValue =
        typeof formData.solde === 'string' ? parseNumericValue(formData.solde) : formData.solde;

      const accountData: Compte = {
        nom: formData.nom.trim(),
        solde: soldeValue,
        solde_initial: soldeValue,
        couleur: formData.couleur,
        type: formData.type,
        actif: formData.actif,
        is_default: formData.is_default,
        ...(formData.description.trim() && { description: formData.description.trim() }),
        ...(formData.iban.trim() && { iban: formData.iban.trim() }),
        ...(formData.bic.trim() && { bic: formData.bic.trim() }),
        ...(formData.numero_compte.trim() && { numero_compte: formData.numero_compte.trim() }),
        ...(formData.nom_banque.trim() && { nom_banque: formData.nom_banque.trim() }),
        site_web: formData.site_web.trim() || null,
      };

      const result = await onSubmit(accountData);

      if (result.success) {
        if (!account?.id && result.data?.id && pendingIconFile) {
          const { error: iconErr } = await commitIconForId(result.data.id, pendingIconFile, null);
          if (iconErr) console.warn('Icône compte:', iconErr);
          else window.dispatchEvent(new Event('suivifinance:comptes-changed'));
        }
        onClose();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const predefinedColors = COLOR_PALETTE;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 border border-dark-700 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-4">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Nom du compte *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => handleInputChange('nom', e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                  placeholder="Ex: Compte principal"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Type de compte</label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                >
                  <option value="compte_courant">Compte courant</option>
                  <option value="epargne">Compte d&apos;épargne</option>
                  <option value="credit">Compte de crédit</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                placeholder="Description optionnelle du compte"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {account?.id ? 'Solde (ajustement)' : 'Solde de départ'}
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.solde}
                onChange={(e) => {
                  const cleaned = cleanNumericInput(e.target.value);
                  handleInputChange('solde', cleaned);
                  if (!account?.id) {
                    handleInputChange('solde_initial', cleaned);
                  }
                }}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                placeholder="0.00"
              />
              <p className="text-dark-400 text-xs mt-1">
                {account?.id
                  ? 'Ce montant est le point de départ du jour. Les dépenses et revenus du même jour (et suivants) mettent le solde à jour automatiquement.'
                  : 'Point de départ du compte. Les transactions du jour et des jours suivants mettent le solde à jour automatiquement.'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">IBAN</label>
                <input
                  type="text"
                  value={formData.iban}
                  onChange={(e) => handleInputChange('iban', e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">BIC/SWIFT</label>
                <input
                  type="text"
                  value={formData.bic}
                  onChange={(e) => handleInputChange('bic', e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                  placeholder="BNPAFRPP"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Numéro de compte</label>
                <input
                  type="text"
                  value={formData.numero_compte}
                  onChange={(e) => handleInputChange('numero_compte', e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                  placeholder="12345678901"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Nom de la banque</label>
                <input
                  type="text"
                  value={formData.nom_banque}
                  onChange={(e) => handleInputChange('nom_banque', e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                  placeholder="Ex: BNP Paribas"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">Site web (portail banque)</label>
              <input
                type="url"
                value={formData.site_web}
                onChange={(e) => handleInputChange('site_web', e.target.value)}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                placeholder="https://client.banque.fr/login"
              />
              <p className="text-dark-400 text-xs mt-1">
                Lien direct vers la page de connexion du portail bancaire.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Couleur</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={formData.couleur}
                    onChange={(e) => handleInputChange('couleur', e.target.value)}
                    className="w-12 h-10 bg-transparent border border-dark-700 rounded-lg cursor-pointer"
                  />
                  <div className="flex space-x-2">
                    {predefinedColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => handleInputChange('couleur', color)}
                        className="w-6 h-6 rounded-full border-2 border-dark-700 hover:border-white transition-colors"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Statut</label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.actif}
                      onChange={(e) => handleInputChange('actif', e.target.checked)}
                      className="w-4 h-4 text-primary-500 bg-dark-800 border-dark-700 rounded focus:ring-primary-500 focus:ring-2"
                    />
                    <span className="text-white text-sm">Compte actif</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => handleInputChange('is_default', e.target.checked)}
                      className="w-4 h-4 text-primary-500 bg-dark-800 border-dark-700 rounded focus:ring-primary-500 focus:ring-2"
                    />
                    <span className="text-white text-sm">Compte par défaut</span>
                  </label>
                  <p className="text-[11px] text-dark-400">
                    Utilisé automatiquement à l&apos;import CSV si aucune colonne compte n&apos;est
                    choisie. Un seul compte par défaut.
                  </p>
                </div>
              </div>
            </div>

            {/* Icône (optionnel) — même logique que les transactions périodiques */}
            <div className="border border-dark-600 rounded-lg p-3 space-y-2">
              <label className="block text-sm font-medium text-white">Icône (optionnel)</label>
              <p className="text-[11px] text-dark-400">
                Logo banque ou image affichée en rond dans la liste des comptes.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleIconPick}
                disabled={attachmentBusy || loading}
              />
              {attachmentPreviewUrl && (
                <div className="w-16 h-16 rounded-full overflow-hidden border border-dark-600 bg-dark-900">
                  <img src={attachmentPreviewUrl} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachmentBusy || loading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-dark-700 text-white rounded-md hover:bg-dark-600 disabled:opacity-50"
                >
                  <ImagePlus className="w-4 h-4" />
                  {attachmentPath || pendingIconFile ? "Remplacer l'icône" : 'Choisir une image'}
                </button>
                {(attachmentPath || pendingIconFile) && (
                  <button
                    type="button"
                    onClick={handleRemoveIcon}
                    disabled={attachmentBusy || loading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-500/15 text-red-300 rounded-md hover:bg-red-500/25 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Retirer
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>

          <div className="shrink-0 flex justify-end space-x-3 px-6 pt-4 pb-6 border-t border-dark-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-dark-400 hover:text-white transition-colors"
              disabled={loading || attachmentBusy}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || attachmentBusy}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountForm;
